  import { Block, Box, extraBreakpoint, transitionsDelay } from '../../../styles/basicStyles';
import { breakpoints } from '../../../styles/breakpoints';
import styled, { css } from 'styled-components';
import Typography from '../../../components/typography';

/*************************************** GRAPH ***************************************/

export const GraphContent = styled.div`
  height: 27.125rem;
  width: calc(100% - 2.875rem);
  position: relative;
  display: flex;
  flex-direction: row;
  border-bottom: 0.125rem dashed ${({ theme }) => theme.colors.dawn};
  border-left: 0.125rem dashed ${({ theme }) => theme.colors.dawn};
  margin-left: 2.875rem;

  &:before {
    content: '';
    position: absolute;
    width: 0; 
    height: 0; 
    border-left: 0.25rem solid transparent;
    border-right: 0.25rem solid transparent;
    border-bottom: 0.5rem solid ${({ theme }) => theme.colors.dawn};
    top: -0.5rem;
    left: -0.313rem;
  }

  &:after {
    content: '';
    position: absolute;
    width: 0; 
    height: 0; 
    border-right: 0.25rem solid transparent;
    border-top: 0.25rem solid transparent;
    border-bottom: 0.25rem solid transparent;
    border-left: 0.5rem solid ${({ theme }) => theme.colors.dawn};
    bottom: -0.313rem;
    right: -0.75rem;
  }

  & svg {
    overflow: visible !important;
  }

  @media ${breakpoints.md} {
    height: 22.25rem;
    width: calc(100% - 1.5rem);
    margin-left: 1.5rem;
  }
`;

export const GraphColumn = styled.div`
  width: 25%;
  margin-top: 1.875rem;
  transition: background-color ${transitionsDelay} linear;

  &:first-child {
    border-right: 0.063rem dashed rgba(129, 108, 255, 0.5);
  }

  &:nth-child(4) {
    border-left: 0.063rem dashed rgba(129, 108, 255, 0.5);
  }
`;

export const GraphColumnTitle = styled(Typography)`
  font-size: 1.313rem;
  line-height: 1.75rem;
  font-weight: 700;
  color: #303030;
  text-align: center;
  transition: color ${transitionsDelay} linear;
  margin-top: 0.5rem;

  @media ${breakpoints.md} {
    font-size: 0.875rem;
    line-height: 1.063rem;
    font-weight: 600;
  }

  @media (max-width: ${extraBreakpoint}rem) {
    font-size: 0.75rem;
    line-height: 1rem;
  }
`;

export const GraphLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 1;

  & svg {
    overflow: visible !important;
  }

  @media ${breakpoints.md} {
    & circle {
      r: 1.563rem;
    }

    & path {
      stroke-width: 4;
    }
  }

  @media ${breakpoints.sm} {
    & circle {
      r: 1.875rem;
    }

    & path {
      stroke-width: 6;
    }
  }

  @media ${breakpoints.xs} {
    & circle {
      r: 2.5rem;
    }

    & path {
      stroke-width: 8;
    }
  }

  @media (max-width: ${extraBreakpoint}rem) {
    & circle {
      r: 3.125rem;
    }

    & path {
      stroke-width: 10;
    }
  }
`;

export const GraphRotateText = styled(Typography)`
  position: absolute;
  top: 50%;
  left: -2rem;
  transform-origin: 0 0;
  transform: rotateZ(-90deg) translateX(-50%);
  
  @media ${breakpoints.md} {
    left: -1.5rem;
  }
`;

export const GraphLabel = styled(Typography)<{ width: number }>`
  font-size: 1.313rem;
  line-height: 1.75rem;
  font-weight: 700;
  text-align: center;
  width: ${({ width }) => width}%;
  color: #595959;

  @media ${breakpoints.md} {
    font-size: 0.875rem;
    line-height: 1.063rem;
    font-weight: 600;
  }

  @media (max-width: ${extraBreakpoint}rem) {
    font-size: 0.75rem;
    line-height: 1rem;
  }
`;

/*************************************** OUR PROCESS ***************************************/

export const ProcessLine = styled.div<{ started: boolean }>`
  position: absolute;
  left: 0;
  bottom: -2rem;
  width: 100%;

  & svg {
    width: 100%;
    height: auto;
  }

  ${({ started }) => started ?
    css`
      display: block; 

      & path {
        stroke-dasharray: 500;
        stroke-dashoffset: 500;
        animation-duration: 2.8s;
        animation-name: dash;
        animation-iteration-count: 1;
        animation-delay: 0;
        animation-fill-mode: forwards;
      }

      @keyframes dash {
        to {
          stroke-dashoffset: 0;
        }
      }
    `
    :
    css`
      display: none;
    `
  }

  @media ${breakpoints.md} {
    bottom: -1.5rem;
  }
`;

export const ProcessCardWrapper = styled(Box)<{onView: boolean}>`
  transition: background-color ${transitionsDelay} linear;
  background-color: ${({ theme, onView }) => onView ? theme.colors.blackCow : theme.colors.darkJungleGreen}; 
  border-radius: 0.25rem;
  flex-direction: column;
`;

export const MinusIconWrapper = styled.span`
  display: inline-block;
  margin-right: 0.375rem;
  vertical-align: middle;
`;

export const RoundIndex = styled(Box)<{onView: boolean}>`
  height: 3.75rem;
  width: 3.75rem;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  transition: background-color, border ${transitionsDelay} linear;
  border: ${({ theme, onView }) => `0.063rem solid ${onView ? theme.colors.purple : theme.colors.white}`}; 
  background-color: ${({ theme, onView }) => onView ? theme.colors.purple : theme.colors.chineseBlack}; 

  @media ${breakpoints.xs} {
    height: 2.75rem;
    width: 2.75rem;
  }
`;

export const OutsideIconWrapper = styled(Block)`
  transform: rotate(180deg);
`;

export const QuestioMarkWrapper = styled.span`
  display: inline-block;
  margin-left: 0.1rem;

  @media ${breakpoints.md} {
    & svg {
      width: 1.1rem;
      height: 1.5rem;
    }
  }
`;

export const ProcessCircle1 = styled.div`
  position: absolute;
  top: -1.625rem;
  left: -4.375rem;
  width: 15rem;
  height: 15rem;
  border-radius: 50%;
  background: radial-gradient(50% 50% at 50% 50%, rgba(100, 255, 227, 0.40) 0%, rgba(100, 255, 227, 0.40) 100%);
  filter: blur(9.375rem);
  z-index: 1;
  // This code is to fix issues with the gradient in IOS
  transform: translateZ(0);
  backface-visibility: hidden;

  @media ${breakpoints.md} {
    width: 7.5rem;
    height: 7.5rem;
    left: -7.25rem;
    top: 70.375rem;
    background: radial-gradient(50% 50% at 50% 50%, rgba(100, 255, 227, 0.60) 0%, rgba(100, 255, 227, 0.60) 100%);
    filter: blur(5.688rem);
  }
`;

export const ProcessCircle2 = styled.div`
  position: absolute;
  top: 7.875rem;
  left: 0rem;
  width: 17.5rem;
  height: 17.5rem;
  border-radius: 50%;
  background: radial-gradient(50% 50% at 50% 50%, rgba(129, 108, 255, 0.30) 0%, rgba(129, 108, 255, 0.30) 100%);
  filter: blur(9.375rem);
  z-index: 1;
  // This code is to fix issues with the gradient in IOS
  transform: translateZ(0);
  backface-visibility: hidden;

  @media ${breakpoints.md} {
    width: 10.625rem;
    height: 10.625rem;
    left: -5.5rem;
    top: 61.063rem;
    background: radial-gradient(50% 50% at 50% 50%, rgba(129, 108, 255, 0.40) 0%, rgba(129, 108, 255, 0.40) 100%);
    filter: blur(4.688rem);
  }
`;

export const ProcessCircle3 = styled.div`
  position: absolute;
  top: 64.125rem;
  right: -4rem;
  width: 15rem;
  height: 15rem;
  border-radius: 50%;
  background: radial-gradient(50% 50% at 50% 50%, rgba(100, 255, 227, 0.40) 0%, rgba(100, 255, 227, 0.40) 100%);
  filter: blur(9.375rem);
  z-index: 1;
  // This code is to fix issues with the gradient in IOS
  transform: translateZ(0);
  backface-visibility: hidden;

  @media ${breakpoints.md} {
    width: 7.5rem;
    height: 7.5rem;
    right: -1.5rem;
    top: 22.375rem;
    background: radial-gradient(50% 50% at 50% 50%, rgba(100, 255, 227, 0.60) 0%, rgba(100, 255, 227, 0.60) 100%);
    filter: blur(5.688rem);
  }
`;

export const ProcessCircle4 = styled.div`
  position: absolute;
  top: 73.438rem;
  right: -5rem;
  width: 17.5rem;
  height: 17.5rem;
  border-radius: 50%;
  background: radial-gradient(50% 50% at 50% 50%, rgba(129, 108, 255, 0.30) 0%, rgba(129, 108, 255, 0.30) 100%);
  filter: blur(9.375rem);
  z-index: 1;
  // This code is to fix issues with the gradient in IOS
  transform: translateZ(0);
  backface-visibility: hidden;

  @media ${breakpoints.md} {
    width: 10.625rem;
    height: 10.625rem;
    right: -6.5rem;
    top: 12.938rem;
    background: radial-gradient(50% 50% at 50% 50%, rgba(129, 108, 255, 0.40) 0%, rgba(129, 108, 255, 0.40) 100%);
    filter: blur(4.688rem);
  }
`;

export const ProcessCircle5 = styled.div`
  position: absolute;
  bottom: -8rem;
  right: 15.25rem;
  width: 15rem;
  height: 15rem;
  border-radius: 50%;
  background: radial-gradient(50% 50% at 50% 50%, rgba(100, 255, 227, 0.40) 0%, rgba(100, 255, 227, 0.40) 100%);
  filter: blur(9.375rem);
  z-index: 1;
  // This code is to fix issues with the gradient in IOS
  transform: translateZ(0);
  backface-visibility: hidden;

  @media ${breakpoints.md} {
    width: 7.5rem;
    height: 7.5rem;
    right: 1.75rem;
    bottom: -0.5rem;
    background: radial-gradient(50% 50% at 50% 50%, rgba(100, 255, 227, 0.60) 0%, rgba(100, 255, 227, 0.60) 100%);
    filter: blur(5.688rem);
  }
`;

export const ProcessCircle6 = styled.div`
  display: none;

  @media ${breakpoints.md} {
    display: block;
    position: absolute;
    bottom: 5rem;
    right: -3rem;
    width: 10.625rem;
    height: 10.625rem;
    border-radius: 50%;
    background: radial-gradient(50% 50% at 50% 50%, rgba(129, 108, 255, 0.40) 0%, rgba(129, 108, 255, 0.40) 100%);
    filter: blur(4.688rem);
    z-index: 1;
    // This code is to fix issues with the gradient in IOS
    transform: translateZ(0);
    backface-visibility: hidden;
  }
`;

export const ProcessCircle7 = styled.div`
  display: none;

  @media ${breakpoints.md} {
    display: block;
    position: absolute;
    top: 123.625rem;
    left: -3rem;
    width: 6.25rem;
    height: 6.25rem;
    border-radius: 50%;
    background: radial-gradient(50% 50% at 50% 50%, rgba(100, 255, 227, 0.80) 0%, rgba(100, 255, 227, 0.80) 100%);
    filter: blur(4.688rem);
    z-index: 1;
    // This code is to fix issues with the gradient in IOS
    transform: translateZ(0);
    backface-visibility: hidden;
  }
`;
